import {effect} from '@angular/core';
import {patchState, signalStoreFeature, type, withHooks} from '@ngrx/signals';
import {GameStoreState} from '../game-state.model';

const STORAGE_KEY = 'gis-de-plek-game-state';

/**
 * Version of the persisted state shape. **Bump this whenever the shape of
 * {@link PersistedState} changes in a backwards-incompatible way** (e.g. a field is
 * renamed, removed, or changes type). On load, persisted data tagged with a different
 * version is discarded rather than rehydrated, preventing stale blobs from crashing the
 * app after a breaking change.
 *
 * Just increment by one in case of breaking changes; no need to use a date or semantic versioning scheme. The version is only used to detect incompatibility, not to track features or releases.
 */
const SCHEMA_VERSION = 4;

/** The envelope actually written to `localStorage`, tagging the payload with its version. */
type VersionedState = {version: number; state: Partial<PersistedState>};

/**
 * The subset of {@link GameStoreState} that is safe to persist across sessions.
 *
 * Excluded:
 * - `currentQuestion` — now a computed signal (derived from `currentGameQuestions` +
 *   `currentQuestionIndex` + `phase`); no longer a state field, so it is not persisted
 *   directly — the values it depends on are persisted and it recomputes on restore.
 * - `error` — transient UI notification; should not survive a refresh.
 * - `guesses` / `answerOptions` — each {@link Answer} contains a
 *   `Feature<Geometry>` (OpenLayers object) that cannot be serialised to JSON.
 *   The map component re-creates these from WFS data on every question load.
 *   Guesses are instead persisted through the serialisable `persistedGuesses`
 *   projection, from which the map rehydrates them after a refresh.
 * - `isInfoModalOpen` — transient UI state; a modal should never re-open itself
 *   after a refresh.
 */
type PersistedState = Omit<GameStoreState, 'error' | 'guesses' | 'answerOptions' | 'isInfoModalOpen'>;

function loadFromStorage(): Partial<PersistedState> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<VersionedState>;
    if (parsed?.version !== SCHEMA_VERSION || !parsed.state) {
      // Incompatible (or legacy, unversioned) data: drop it so a breaking change to the
      // persisted shape can never rehydrate into the store and crash the app.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.state;
  } catch {
    return null;
  }
}

/**
 * Reads and persists the game state to/from `localStorage` across browser
 * refreshes.  Placed just before the store's own `withHooks` so that
 * rehydration happens synchronously before any reactive effects fire.
 */
export function withLocalStoragePersistence() {
  return signalStoreFeature(
    type<{state: GameStoreState}>(),
    withHooks({
      onInit(store) {
        // Synchronously rehydrate before any reactive effects run.
        const saved = loadFromStorage();
        if (saved) {
          patchState(store, saved);
        }

        // Write the persisted subset to localStorage on every change.
        effect(() => {
          const snapshot: PersistedState = {
            phase: store.phase(),
            themeId: store.themeId(),
            currentQuestionIndex: store.currentQuestionIndex(),
            questionState: store.questionState(),
            currentGameQuestions: store.currentGameQuestions(),
            finishedQuestionStates: store.finishedQuestionStates(),
            previousGamesQuestions: store.previousGamesQuestions(),
            showAnswerOnMap: store.showAnswerOnMap(),
            persistedGuesses: store.persistedGuesses(),
            answer: store.answer(),
            answerLegendUrl: store.answerLegendUrl(),
            score: store.score(),
          };
          try {
            const versioned: VersionedState = {version: SCHEMA_VERSION, state: snapshot};
            localStorage.setItem(STORAGE_KEY, JSON.stringify(versioned));
          } catch {
            // Ignore quota-exceeded or unavailable storage.
          }
        });
      },
    })
  );
}
